<template>
    <div>
        <b-form-group>
            <label><b-icon icon="arrow-right-square-fill"></b-icon> <span v-html="title"></span></label>
            <b-form-file
                :id="id"
                v-model="file"
                :state="stateFileInput"
                placeholder="Choisir un fichier au format JPEG, PNG ou PDF"
                drop-placeholder="Déposer votre fichier ici"
                browse-text="Parcourir..."
                :accept="accept.toString()"
                @change="handleUpload($event)"
                :disabled="disabled"
            ></b-form-file>
            <b-form-text v-if="this.errorMessage !== null">
                <b-badge variant="danger">{{ this.errorMessage }}</b-badge>
            </b-form-text>
            <b-form-text v-else-if="this.remaining > 0">
                Vous pouvez déposer encore {{ remaining | pluralize(['fichier', 'fichiers']) }}
            </b-form-text>
        </b-form-group>

        <b-row v-for="(obj, i) in fileUploader.files" :key="i" align-v="center" class="mb-2">
            <b-col cols="6"><b-icon icon="check2"></b-icon> {{ obj.file.name }}</b-col>
            <b-col cols="2">{{ obj.file.size | sizeFormat }}</b-col>
            <b-col cols="3"><b-progress :value="obj.uploadPercentage" :max="100" :animated="animated(i)"></b-progress></b-col>
            <b-col cols="1">
                <b-button variant="outline-danger" v-if="deletingProgress(i)">
                    <b-icon icon="trash" animation="fade"></b-icon>
                </b-button>
                <b-button variant="outline-danger" v-b-modal="'delete-file-' + id + '-' + i" v-else>
                    <b-icon icon="trash"></b-icon>
                </b-button>

                <b-modal :id="'delete-file-' + id + '-' + i" title="Confirmer la suppression" @ok="deleteFile(i)" centered cancel-title="Annuler" ok-variant="danger">
                    <p class="my-2">Supprimer le fichier {{ obj.file.name }} ?</p>
                </b-modal>
            </b-col>
        </b-row>
    </div>
</template>

<script>
export default {
    name: 'FileUploader',
    props: [
        'id',
        'title',
        'nbFiles'
    ],
    created() {
        // Test si la variable est déjà déclarée dans le store avant de l'initialiser le cas échéant
        if (typeof this.$store.fileUploader[this.id] === 'undefined') {
            // Création dynamique dans le store avec l'id passé en props: Cet indice de tableau contiendra toutes les données propres a ce composant
            this.$set(this.$store.fileUploader, this.id, []);
            this.$set(this.$store.fileUploader[this.id], 'files', []);
            this.$set(this.$store.fileUploader[this.id], 'state', null);
        }
    },
    data() {
        return {
            file: null,
            accept: this.$config.FILE_TYPE_ACCEPT,
            maxSize: this.$config.MAX_FILE_SIZE,
            errorMessage: null
        }
    },
    computed: {
        fileUploader: {
            get() {
                return this.$store.fileUploader[this.id];
            },
            set(val) {
                this.$store.fileUploader[this.id] = val
            }
        },
        // Statut global du composant
        state: {
            get() {
                return this.fileUploader.state;
            },
            set(val) {
                return this.fileUploader.state = val
            }
        },
        // Statut servant uniquement a coloriser l'input selon la validité des fichiers
        stateFileInput: function() {
            if (this.errorMessage !== null) {
                return false
            } else if (this.state) {
                return true
            }
            return null
        },
        // Propriétés calculées prenant un paramètre "i"
        animated() {
            return (i) => {
                return (this.fileUploader.files[i].uploadPercentage == 100) ? false : true;
            }
        },
        deletingProgress() {
            return (i) => {
                return this.fileUploader.files[i].deletingProgress;
            }
        },
        // Calcul le nb de fichiers encore disponible a charger
        remaining: function() {
            return this.nbFiles.max - this.fileUploader.files.length;
        },
        // Désactive le champ lorsque le nombre de fichier est atteint
        disabled: function() {
            return (this.remaining > 0) ? false : true;
        }
    },
    methods: {
        // Test parmi les fichiers uploadés si le nom du fichier existe déjà
        isFileNameExists(fileName) {
            for (var key in this.fileUploader.files) {
                if (this.fileUploader.files[key].file.name == fileName) {
                    return true
                }
            }
            return false
        },
        handleUpload(event) {
            // Recupération du fichier selectionné
            var file = event.target.files[0];
            this.errorMessage = null;

            try {
                if (!this.accept.includes(file.type)) {
                    throw "Le format de fichier n'est pas celui attendu (JPEG, PNG ou PDF)";
                } else if (file.size > this.maxSize) {
                    throw "Le fichier dépasse la taille maximale autorisée de " + this.$options.filters.sizeFormat(this.maxSize);
                } else if (file.name.length > 255) {
                    throw "Le nom du fichier est trop long";
                } else if (this.isFileNameExists(file.name)) {
                    throw "Le fichier " + file.name + " existe déjà";
                } else if (file.size < 1) {
                    throw "Le fichier est vide";
                }

                // Ajoute le fichier dans les files du fileUploader
                this.fileUploader.files.push( {'file': file} );
                let i = Object.keys(this.fileUploader.files).pop();

                // Envoi le fichier vers le serveur
                this.uploadFile(file, i);
            } catch (e) {
                this.errorMessage = e;
            }
        },

        uploadFile(file, i) {
            let formData = new FormData();
            formData.append('file', file);
            formData.append('natureDocument', this.id);

            // Prepare un objet abort controller dont le signal sera passé plus loin dans la config
            const controller = new AbortController();
            this.$set(this.fileUploader.files[i], 'controller', controller);

            const config = {
                // Upload
                onUploadProgress: event => {
                    this.$set(this.fileUploader.files[i], 'uploadPercentage', Math.round((event.loaded * 100) / event.total));
                },
                // Signal, pour effecter un éventuel abort de l'upload
                signal: this.fileUploader.files[i].controller.signal,
                // Header contenant notamment le token
                headers: {
                    'Content-Type': 'multipart/form-data',
                    // Force une chaine vide si la valeur du token est null (pour éviter d'envoyer une valeur === 'null' dans PHP)
                    'Authorization': (this.$store.token === null) ? '' : this.$store.token
                }
            };

            this.$axios.post(this.$config.API + '/upload-file',
                formData,
                config,
                ).then(response => {
                    // En cas de response ok du traitement serveur, on iscrit l'id retourné au fileUploader
                    if (response.data.status == 1) {
                        this.$set(this.fileUploader.files[i], 'id', response.data.id);
                        this.setState();
                    } else {
                        console.log(response.data.message);
                    }
                })
                .catch();
        },

        deleteFile(i) {
            // Test si le chargement du fichier est terminé ou pas
            if (this.fileUploader.files[i].uploadPercentage < 100) {
                // Interruption de l'envoi en cours du fichier
                this.fileUploader.files[i].controller.abort();
            // Si le chargement est terminé, alors on ira supprimer le fichier physiquement sur le serveur
            } else {

                // Crée la variable dans le fileUploader qui permet de visulaliser l'animation d'attente du delete
                this.$set(this.fileUploader.files[i], 'deletingProgress', true);

                // Id lié au fichier uploadé
                let id = this.fileUploader.files[i].id;

                const config = {
                    // Header contenant notamment le token
                    headers: {
                        'Authorization': this.$store.token
                    }
                };

                this.$axios.delete(this.$config.API + '/delete-file/' + id,
                    config,
                    ).then(response => {
                        if (response.data.status == 1) {
                            // Remet le file à null pour permettre de remettre l'affiche initial dans l'input file
                            this.file = null;
                            // Supprime le file dans le $store fileUploader, et donc la ligne dans l'affichage
                            this.fileUploader.files.splice(i, 1);
                            this.setState();
                        } else {
                            console.log(response.data.message);
                            // Stop l'animation en cas d'erreur
                            this.fileUploader.files[i].deletingProgress = false;
                        }
                    })
                    .catch();
            }
        },

        // Defini et calcul le status global du composant et renvoi le $emit en "surface" vers le parent
        setState: function() {
            // Parcours l'ensemble des files afin de s'assurer qu'ils soient tous chargés à 100%
            var tx1 = true;
            this.fileUploader.files.forEach(file => {
                if (file.uploadPercentage < 100) {
                    tx1 = false;
                    return false;
                }
            });
            // Si des fichiers existent, qu'ils sont tous chargés a 100% et que le nombre est au moins égal au nombre de fichiers mini attendu
            if (this.fileUploader.files.length > 0 && tx1 && this.fileUploader.files.length >= this.nbFiles.min) {
                this.state = true;
            } else {
                this.state = false;
            }
            this.$emit('onState', this.state);
        }

    },
    filters: {
        // Filtre local pour formatter une taille de fichier
        sizeFormat: function(size) {
            if (size >= 1073741824) {
                size = (size/1073741824).toFixed(2) + ' Go';
            } else if (size >= 1048576) {
                size = (size/1048576).toFixed(1) + ' Mo';
            } else if (size >= 1024) {
                size = (size/1024).toFixed(0) + ' Ko';
            } else {
                size = size + ' o';
            }
            return size;
        },
        // Filtre permettant de mettre au pluriel si besoin
        pluralize: function(value, choices, showValue=true) {
            // S'il y a 0 ou 1 élément, on met au singulier, sinon au pluriel
            let word = (value <= 1) ? choices[0] : choices[1];
            // Si showValue vaut true, affichage de la valeur avant le mot
            return showValue ? value + ' ' + word : word;
        }
    }

}
</script>



// WEBPACK FOOTER //
// src/components/FileUploader.vue